import { useDragOver } from '@minoru/react-dnd-treeview'

import { ReactComponent as MusicIcon } from 'assets/icons/music.svg'
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg'
import { ReactComponent as RemoveIcon } from 'assets/icons/close.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { ReactComponent as StarIcon } from 'assets/icons/star.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { ReactComponent as ArtIcon } from 'assets/icons/art.svg'
import { ReactComponent as ShareIcon } from 'assets/icons/share.svg'
import { ReactComponent as UpdateIcon } from 'assets/icons/update.svg'
import { ReactComponent as PasteIcon } from 'assets/icons/paste.svg'

import DotsMenu from 'components/DotsMenu/DotsMenu'
import clsx from 'clsx'
import LoadingBlocker from '../../components/LoadingSpinner/LoadingBlocker'

const PlaylistNode = (props) => {
  const type = props.node.id.slice(0, 1)
  switch (type) {
    case 'p':
      return <PlaylistItem {...props} />
    case 'g':
      return <GroupItem {...props} />
    case 't':
      return <TrackItem {...props} />
    default:
      return null
  }
}

const PlaylistItem = (props) => {
  const { id } = props.node
  const {
    isOpen,
    isLoading,
    isOutOfDate,
    onToggle,
    hasChild,
    hidePlaylistOptions,
    copyBuffer,
  } = props
  const dragOverProps = useDragOver(id, isOpen, onToggle)
  const blockPublish = isLoading || isOutOfDate
  const isThemed = !!props.node?.data?.theme
  const indent = props.depth * 24
  const handleToggle = (e) => {
    if (!hidePlaylistOptions) {
      e.stopPropagation()
      props.onToggle(id)
    }
  }

  function handleAddGroupClick(evt) {
    evt.stopPropagation()
    if (props.onAddGroupClick !== undefined) {
      props.onAddGroupClick(props.node.id)
    }
  }

  function handlePaste(evt) {
    evt.stopPropagation()
    if (props.onPasteClick !== undefined) {
      props.onPasteClick(props.node.id)
    }
  }

  function handleEditClick() {
    props.onEditClick(props.node, props)
  }

  function handleRemoveClick() {
    props.onRemoveClick(props.node, props)
  }

  function handleGetLinkClick(evt) {
    evt.stopPropagation()
    props.onGetLinkClick(props.node, props)
  }

  function handlePublishChanges(evt) {
    props.onPublishChangesClick(props.node, props)
    evt.stopPropagation()
  }

  function handleSetTrackTheme(evt) {
    props.onSetPlayerThemeClick(props.node, props)
    evt.stopPropagation()
  }

  return (
    <div
      className={clsx('tree-node playlist-node w-full ')}
      style={{ paddingInlineStart: indent }}
      {...dragOverProps}
    >
      <div
        className={clsx(
          'bg-gray-blue flex flex-row group transition hover:bg-gray-blue-light cursor-pointer z-10 ',
          {
            hidden: hidePlaylistOptions,
          },
        )}
        onClick={handleToggle}
      >
        <div
          className={clsx(
            isOpen ? 'max-w-[10px]' : 'max-w-[0px] group-hover:max-w-[3px]',
            'bg-pink transition-all w-full',
          )}
        ></div>

        <div
          className={clsx(
            'px-4 h-[55px] flex flex-row flex-1 items-center transition-all',
          )}
        >
          <div className="flex-1 text-xl tracking-wide">{props.node.text}</div>

          {props?.node?.data?.pendingChanges && (
            <div className="">
              <button
                className="button icon relative z-0 mr-2"
                onClick={handlePublishChanges}
                disabled={blockPublish}
                data-tooltip="Publish Changes"
              >
                <UpdateIcon fill="#fff" height="22" />
                {blockPublish && <LoadingBlocker size="tiny" />}
              </button>
            </div>
          )}
          <button
            onClick={handleGetLinkClick}
            disabled={blockPublish}
            className="button icon relative z-0"
            data-tooltip="Playlist Embedded Code"
          >
            <ShareIcon fill="#e2e4e9" height="22" />
            {blockPublish && <LoadingBlocker size="tiny" />}
          </button>

          <button
            className="ml-4 mr-2"
            data-tooltip="Style Player"
            onClick={handleSetTrackTheme}
          >
            <ArtIcon fill="#ccc" />
          </button>

          <div className="ml-2">
            <DotsMenu>
              <button onClick={handleEditClick}>Rename</button>
              <button onClick={handleRemoveClick}>Delete</button>
            </DotsMenu>
          </div>
        </div>
      </div>
      {isOpen && (
        <div className="bg-gray-dark h-[40px] w-full flex items-center px-6">
          <button
            className="relative flex"
            onClick={handleAddGroupClick}
            disabled={props.isEditGroup}
            data-tooltip="Add Folder"
            type="button"
          >
            <FolderIcon fill="#ccc" />
            <div className="absolute text-white text-xl bg-gray-dark rounded w-[15px] h-[15px] flex items-center justify-center right-0 bottom-[2px]">
              <PlusIcon fill="#fff" width="12" heigh="13" />
            </div>
          </button>

          <button
            onClick={handlePaste}
            data-tooltip={`Paste Item${
              copyBuffer.length > 1 ? 's' : ''
            } (Ctrl/Cmd + V)`}
            className={clsx('ml-4', {
              'opacity-60 pointer-events-none': copyBuffer?.length === 0,
            })}
          >
            <PasteIcon fill="#eee" width="22" height="22" />
          </button>
        </div>
      )}

      {isOpen && !hasChild && (
        <div className="bg-gray flex flex-row justify-center items-center p-6">
          <div>
            <h3 className="text-white-dark mb-2">Empty Playlist</h3>
            <p>Drag in a track or add a group to get started</p>
          </div>
        </div>
      )}
    </div>
  )
}

const GroupItem = (props) => {
  const { node, isSelected, onItemClick } = props
  const { id, text, data } = node
  const art = data?.art
  const { isOpen, onToggle, hasChild } = props
  const dragOverProps = useDragOver(id, isOpen, onToggle)
  const indent = props.depth * 24
  const handleToggle = (e) => {
    e.stopPropagation()
    if (hasChild) {
      props.onToggle(id)
    }
  }

  function handleEditClick(evt) {
    evt.stopPropagation()
    props.onEditClick(node, props)
  }

  function handleRemoveClick(evt) {
    evt.stopPropagation()
    props.onRemoveClick(node, props)
  }

  const iconColor = isSelected ? '#eee' : '#ccc'

  return (
    <div
      className={clsx(
        'tree-node group-node w-full transition flex items-center h-[50px] pr-14 cursor-pointer',
        {
          'py-2': !isOpen || !hasChild,
          'bg-dark hover:bg-gray-lighter text-white-dark': !isSelected,
          'bg-pink-dark outline outline-1 outline-pink hover:bg-pink-med text-white':
            isSelected,
        },
      )}
      style={{ paddingInlineStart: indent }}
      {...dragOverProps}
      onMouseDown={(evt) => onItemClick(evt, node)}
      onDoubleClick={handleEditClick}
      onClick={(evt) => onItemClick(evt, node)}
    >
      <div
        className="mr-4"
        onClick={handleToggle}
        onDoubleClick={(evt) => evt.stopPropagation()}
        onMouseDown={(evt) => evt.stopPropagation()}
      >
        <div
          className={clsx('chevron', {
            bottom: !isOpen,
            up: isOpen,
          })}
        ></div>
      </div>
      <div className="w-[35px] h-[35px] flex justify-center items-center  ">
        {art ? (
          <img src={art} className=" w-[35px] h-[35px] object-cover rounded" />
        ) : (
          <FolderIcon fill="#b3b4b5" height="24" width="24" />
        )}
      </div>
      <div className="px-2 leading-none flex-1 text-lg tracking-wider text-text-white">
        {text}
      </div>

      <div className="node-actions">
        <button
          onClick={handleEditClick}
          data-tooltip="Edit Folder"
          onMouseDown={(evt) => evt.stopPropagation()}
        >
          <EditIcon fill={iconColor} height="20" width="20" />
        </button>
        <button
          onClick={handleRemoveClick}
          data-tooltip="Remove Folder"
          onMouseDown={(evt) => evt.stopPropagation()}
        >
          <RemoveIcon fill={iconColor} height="22" width="22" />
        </button>
      </div>
    </div>
  )
}

const TrackItem = (props) => {
  const indent = props.depth * 24
  const { node, isSelected, onItemClick, onItemDoubleClick } = props
  const isFavourite = node.data.selected
  const iconColor = isSelected ? '#eee' : '#ccc'
  const isThemed = !!node.data?.theme

  function handleThemeClick(evt) {
    props.onSetTrackThemeClick(node, props)
  }

  function handleRenameClick(evt) {
    props.onSetTrackNameClick(node, props)
  }
  function handleFavouriteClick(evt) {
    props.onFavouriteClick(node, props)
  }

  function handleRemoveClick(evt) {
    props.onRemoveClick(node, props)
  }

  return (
    <div
      className={clsx(
        'tree-node track-node w-full transition flex items-center h-[40px] pr-14 cursor-pointer',
        {
          'bg-dark hover:bg-gray-lighter text-white-dark': !isSelected,
          'bg-pink-dark outline outline-1 outline-pink hover:bg-pink-med text-white':
            isSelected,
        },
      )}
      style={{ paddingInlineStart: indent }}
      onMouseDown={(evt) => onItemClick(evt, node)}
      onClick={(evt) => onItemClick(evt, node)}
      onDoubleClick={() => onItemDoubleClick(node)}
    >
      <div className="flex items-center">
        <div className="flex items-center">
          <MusicIcon fill="#CCC" height="12" width="12" />
        </div>
        {isFavourite && (
          <div className="flex items-center ml-2">
            <StarIcon fill="#d63b5c" height="14" width="14" />
          </div>
        )}
        {isThemed && (
          <div className="flex items-center ml-2">
            <ArtIcon fill="#d63b5c" height="14" width="14" />
          </div>
        )}
      </div>

      <div
        className={clsx('px-2 leading-none text-sm flex-fill text-white', {})}
      >
        {node?.data?.customName || node.text}
      </div>
      <div className="node-actions">
        <button
          onClick={handleRenameClick}
          onMouseDown={(evt) => evt.stopPropagation()}
          data-tooltip="Rename track"
        >
          <EditIcon fill={iconColor} height="16" width="16" />
        </button>
        <button
          onClick={handleThemeClick}
          onMouseDown={(evt) => evt.stopPropagation()}
          data-tooltip="Override track theme"
        >
          <ArtIcon fill={iconColor} height="20" width="20" />
        </button>
        <button
          onClick={handleFavouriteClick}
          onMouseDown={(evt) => evt.stopPropagation()}
          data-tooltip="Set as default track"
        >
          <StarIcon fill={iconColor} height="20" width="20" />
        </button>
        <button
          onClick={handleRemoveClick}
          onMouseDown={(evt) => evt.stopPropagation()}
          data-tooltip="Remove Track"
        >
          <RemoveIcon fill={iconColor} height="20" width="20" />
        </button>
      </div>
    </div>
  )
}

export default PlaylistNode
